'use client'

import {
    FinancialTransfer,
    FinancialTransferStatus,
    getTransfers,
    requestTransfer,
    RequestTransferData,
    TransferDestinationType,
} from '@/lib/api/financial/transfer-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useEffect, useMemo, useRef, useState } from 'react'
import { MdAdd, MdRefresh, MdSwapHoriz } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import CurrencyInput from '../payment-account/components/currency-input'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import SearchableSelect, {
    SearchableSelectOption,
} from '@/components/searchable-select'
import { getSuppliers, Supplier } from '@/lib/api/suppliers/supplier-api'

const KIND_LABEL: Record<string, string> = {
    STANDALONE: 'Avulsa',
    BILLING_PAYMENT: 'Pagamento de conta',
}

const DESTINATION_LABELS: Record<TransferDestinationType, string> = {
    PIX: 'Chave PIX avulsa',
    BANK_ACCOUNT: 'Conta bancária cadastrada',
    ORG_OWN_BANK: 'Conta própria da empresa',
    PARTNER: 'Parceiro',
    PROFESSIONAL: 'Profissional',
    SUPPLIER: 'Fornecedor',
}

const DESTINATION_HINT: Record<TransferDestinationType, string> = {
    PIX: 'Você informa a chave PIX do destinatário diretamente.',
    BANK_ACCOUNT: 'Selecione uma conta bancária previamente cadastrada.',
    ORG_OWN_BANK: 'Saque para uma conta da própria empresa.',
    PARTNER: 'Envia para parceiro cadastrado. Se ele não tem dados bancários no cadastro, informe-os abaixo.',
    PROFESSIONAL: 'Envia para profissional cadastrado. Se ele não tem dados bancários no cadastro, informe-os abaixo.',
    SUPPLIER: 'Envia para fornecedor cadastrado.',
}

const STATUS_LABEL: Record<FinancialTransferStatus, string> = {
    PENDING_APPROVAL: 'Aguardando aprovação',
    APPROVED: 'Aprovada',
    REJECTED: 'Rejeitada',
    PROCESSING: 'Processando',
    DONE: 'Concluída',
    FAILED: 'Falhou',
    CANCELLED: 'Cancelada',
}

const STATUS_BADGE: Record<FinancialTransferStatus, string> = {
    PENDING_APPROVAL: 'badge-warning',
    APPROVED: 'badge-info',
    REJECTED: 'badge-error',
    PROCESSING: 'badge-info',
    DONE: 'badge-success',
    FAILED: 'badge-error',
    CANCELLED: 'badge-ghost',
}

const formatCurrency = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const formatDateTime = (iso: string): string =>
    new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })

export default function TransfersPage() {
    const navigate = useNavigate()
    const { canCreate } = useResourcePermissions('financial', 'transfers')
    const [transfers, setTransfers] = useState<FinancialTransfer[]>([])
    const [loading, setLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState<FinancialTransferStatus | ''>('')

    const modalRef = useRef<HTMLDialogElement>(null)
    const [submitting, setSubmitting] = useState(false)
    const [form, setForm] = useState<RequestTransferData>({
        destinationType: 'PIX',
        amount: 0,
        pixKey: '',
        pixKeyType: 'CPF',
    })
    const [suppliers, setSuppliers] = useState<Supplier[]>([])

    useEffect(() => {
        // Carrega lista de fornecedores uma vez pra alimentar o SearchableSelect.
        getSuppliers().then((res) => {
            if (res.success && res.result) setSuppliers(res.result)
        })
    }, [])

    const supplierOptions = useMemo<SearchableSelectOption[]>(
        () =>
            suppliers.map((s) => ({
                value: String(s.id),
                label: s.name,
                description: (s as any).cpfCnpj || (s as any).document || undefined,
            })),
        [suppliers],
    )

    useEffect(() => {
        load()
    }, [statusFilter])

    const load = async () => {
        setLoading(true)
        try {
            const res = await getTransfers({ status: statusFilter || undefined })
            if (res.success && res.result) setTransfers(res.result)
        } finally {
            setLoading(false)
        }
    }

    const openModal = () => {
        setForm({ destinationType: 'PIX', amount: 0, pixKey: '', pixKeyType: 'CPF' })
        modalRef.current?.showModal()
    }

    const handleSubmit = async () => {
        if (form.amount <= 0) {
            toast.warning('Informe o valor')
            return
        }
        if (form.destinationType === 'PIX' && (!form.pixKey || !form.pixKeyType)) {
            toast.warning('Informe a chave PIX e o tipo')
            return
        }
        if (form.destinationType === 'PARTNER' && !form.partnerId) {
            toast.warning('Selecione o parceiro')
            return
        }
        if (form.destinationType === 'PROFESSIONAL' && !form.professionalId) {
            toast.warning('Selecione o profissional')
            return
        }
        if (form.destinationType === 'SUPPLIER' && !form.supplierId) {
            toast.warning('Selecione o fornecedor')
            return
        }
        if ((form.destinationType === 'BANK_ACCOUNT' || form.destinationType === 'ORG_OWN_BANK')
            && !form.bankAccountId) {
            toast.warning('Informe a conta bancária')
            return
        }

        setSubmitting(true)
        try {
            const res = await requestTransfer(form)
            if (res.success) {
                toast.success('Transferência solicitada — aguardando aprovação')
                modalRef.current?.close()
                load()
            } else {
                toast.error(res.error || 'Erro ao solicitar transferência')
            }
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <MdSwapHoriz /> Transferências
                    </h1>
                    <p className="text-base-content/60 text-sm mt-1">
                        Saídas da conta digital (PIX, TED, saques, repasses).
                    </p>
                </div>
                <div className="flex gap-2">
                    <button className="btn btn-sm btn-ghost" onClick={load}>
                        <MdRefresh /> Atualizar
                    </button>
                    {canCreate && (
                        <button className="btn btn-secondary" onClick={openModal}>
                            <MdAdd /> Nova transferência
                        </button>
                    )}
                </div>
            </div>

            <div className="card bg-base-100 shadow mb-4">
                <div className="card-body p-4">
                    <div className="form-control max-w-xs">
                        <label className="label">
                            <span className="label-text">Filtrar por status</span>
                        </label>
                        <select
                            className="select select-bordered select-sm"
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value as any)}
                        >
                            <option value="">Todas</option>
                            {Object.entries(STATUS_LABEL).map(([k, l]) => (
                                <option key={k} value={k}>{l}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="card bg-base-100 shadow">
                <div className="card-body p-0">
                    {loading && (
                        <div className="flex items-center justify-center min-h-[200px]">
                            <div className="loading loading-spinner loading-lg text-secondary"></div>
                        </div>
                    )}
                    {!loading && transfers.length === 0 && (
                        <div className="text-center py-12 text-base-content/60">
                            Nenhuma transferência encontrada.
                        </div>
                    )}
                    {!loading && transfers.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Tipo</th>
                                        <th>Destino</th>
                                        <th>Descrição</th>
                                        <th>Status</th>
                                        <th className="text-right">Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transfers.map(t => (
                                        <tr key={t.id} className="hover cursor-pointer" onClick={() => navigate(`/financial/transfers/${t.id}`)}>
                                            <td className="whitespace-nowrap">{formatDateTime(t.createdAt)}</td>
                                            <td className="text-xs">
                                                <span className="badge badge-ghost badge-sm">
                                                    {KIND_LABEL[t.kind] ?? t.kind}
                                                </span>
                                            </td>
                                            <td className="text-sm">
                                                {DESTINATION_LABELS[t.destinationType] ?? t.destinationType}
                                            </td>
                                            <td className="text-sm">{t.description || '—'}</td>
                                            <td>
                                                <span className={`badge badge-sm ${STATUS_BADGE[t.status]}`}>
                                                    {STATUS_LABEL[t.status]}
                                                </span>
                                            </td>
                                            <td className="text-right font-mono whitespace-nowrap text-error">
                                                {formatCurrency(Math.abs(t.amount))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <dialog ref={modalRef} className="modal">
                <div className="modal-box max-w-lg">
                    <h3 className="font-bold text-lg mb-4">Nova transferência</h3>
                    <div className="space-y-4">
                        {/* 1. Tipo de destino */}
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-base-content/80">Tipo de destino</span>
                            <select
                                className="select select-bordered"
                                value={form.destinationType}
                                onChange={(e) => setForm({ ...form, destinationType: e.target.value as TransferDestinationType })}
                            >
                                {(Object.keys(DESTINATION_LABELS) as TransferDestinationType[]).map((k) => (
                                    <option key={k} value={k}>
                                        {DESTINATION_LABELS[k]}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-base-content/60">{DESTINATION_HINT[form.destinationType]}</p>
                        </div>

                        {/* 2. Destinatário — depende do tipo selecionado */}
                        {form.destinationType === 'PIX' && (
                            <>
                                <div className="flex flex-col gap-1">
                                    <span className="text-sm font-medium text-base-content/80">Tipo da chave</span>
                                    <select
                                        className="select select-bordered"
                                        value={form.pixKeyType ?? 'CPF'}
                                        onChange={(e) => setForm({ ...form, pixKeyType: e.target.value as any })}
                                    >
                                        <option value="CPF">CPF</option>
                                        <option value="CNPJ">CNPJ</option>
                                        <option value="EMAIL">Email</option>
                                        <option value="PHONE">Telefone</option>
                                        <option value="EVP">Chave aleatória</option>
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <span className="text-sm font-medium text-base-content/80">Chave PIX</span>
                                    <input
                                        className="input input-bordered"
                                        value={form.pixKey ?? ''}
                                        onChange={(e) => setForm({ ...form, pixKey: e.target.value })}
                                    />
                                </div>
                            </>
                        )}

                        {form.destinationType === 'PARTNER' && (
                            <PartnerSelector
                                value={form.partnerId ?? null}
                                onChange={(id) => setForm({ ...form, partnerId: id ?? undefined })}
                                placeholder="Buscar parceiro..."
                            />
                        )}

                        {form.destinationType === 'PROFESSIONAL' && (
                            <ProfessionalSearchSelector
                                value={form.professionalId ?? null}
                                onChange={(id) => setForm({ ...form, professionalId: id ?? undefined })}
                                placeholder="Buscar profissional..."
                            />
                        )}

                        {form.destinationType === 'SUPPLIER' && (
                            <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-base-content/80">Fornecedor</span>
                                <SearchableSelect
                                    options={supplierOptions}
                                    value={form.supplierId != null ? String(form.supplierId) : ''}
                                    onChange={(value) =>
                                        setForm({
                                            ...form,
                                            supplierId: value ? Number(value) : undefined,
                                        })
                                    }
                                    placeholder="Selecione um fornecedor"
                                    searchPlaceholder="Buscar por nome ou documento..."
                                />
                            </div>
                        )}

                        {(form.destinationType === 'BANK_ACCOUNT' || form.destinationType === 'ORG_OWN_BANK') && (
                            <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-base-content/80">Conta bancária</span>
                                <input
                                    type="number"
                                    className="input input-bordered"
                                    placeholder="ID da conta cadastrada"
                                    value={form.bankAccountId ?? ''}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            bankAccountId: e.target.value
                                                ? Number(e.target.value)
                                                : undefined,
                                        })
                                    }
                                />
                                <p className="text-xs text-base-content/60">
                                    Em breve teremos seleção visual — por enquanto, use o ID exibido na tela de contas bancárias.
                                </p>
                            </div>
                        )}

                        {/* Dados bancários opcionais quando o destinatário cadastrado não tem dados ou são diferentes */}
                        {(form.destinationType === 'PROFESSIONAL' || form.destinationType === 'SUPPLIER' || form.destinationType === 'PARTNER') && (
                            <div className="collapse collapse-arrow border border-base-300 bg-base-100">
                                <input type="checkbox" />
                                <div className="collapse-title text-sm font-medium">
                                    Informar dados bancários diferentes (opcional)
                                </div>
                                <div className="collapse-content space-y-3">
                                    <p className="text-xs text-base-content/60">
                                        Use estes campos apenas se o cadastro do destinatário não tiver dados bancários ou se você quer enviar para outra conta dele.
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs text-base-content/70">Tipo da chave</span>
                                            <select
                                                className="select select-bordered select-sm"
                                                value={form.pixKeyType ?? ''}
                                                onChange={(e) => setForm({ ...form, pixKeyType: (e.target.value || undefined) as any })}
                                            >
                                                <option value="">(nenhuma)</option>
                                                <option value="CPF">CPF</option>
                                                <option value="CNPJ">CNPJ</option>
                                                <option value="EMAIL">Email</option>
                                                <option value="PHONE">Telefone</option>
                                                <option value="EVP">Chave aleatória</option>
                                            </select>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs text-base-content/70">Chave PIX</span>
                                            <input
                                                className="input input-bordered input-sm"
                                                value={form.pixKey ?? ''}
                                                onChange={(e) => setForm({ ...form, pixKey: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 3. Valor */}
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-base-content/80">Valor</span>
                            <CurrencyInput
                                valueCents={form.amount || null}
                                onChange={(cents) => setForm({ ...form, amount: cents ?? 0 })}
                                disabled={submitting}
                                className="input input-bordered"
                            />
                        </div>

                        {/* 4. Descrição */}
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-base-content/80">Descrição (opcional)</span>
                            <input
                                className="input input-bordered"
                                value={form.description ?? ''}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="modal-action">
                        <button className="btn btn-ghost" onClick={() => modalRef.current?.close()} disabled={submitting}>
                            Cancelar
                        </button>
                        <button className="btn btn-secondary" onClick={handleSubmit} disabled={submitting}>
                            {submitting ? <span className="loading loading-spinner loading-sm"></span> : 'Solicitar'}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>
        </div>
    )
}
